import type { CartItem as CartItemType } from '@frontend/libs/types/cart'

import NewArrival from '@frontend/features/products/components/NewArrival'
import { ProductImage } from '@frontend/features/products/components/ProductImage'
import { ImageSize } from '@frontend/libs/types/media'
import { formatPrice } from '@frontend/libs/utils/format-price'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { Button, Input } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { Link } from 'react-router-dom'

import useCartItem from '../libs/hook'

const PriceDisplay = ({
  price,
  regularPrice,
}: { price: number; regularPrice: number }) => (
  <div>
    {regularPrice > price && (
      <span className="block text-sm font-normal text-primary/50 line-through">
        {formatPrice(regularPrice)}
      </span>
    )}
    <span className="block">{formatPrice(price)}</span>
  </div>
)

const CartItem = ({ item }: { item: CartItemType }) => {
  const {
    images,
    name,
    newArrival,
    permalink,
    prices,
    quantityLimits,
    variation,
  } = item
  const { handleRemoveItem, handleUpdateItem, isLoading, quantity } =
    useCartItem(item)

  // concat variation values with comma
  if (variation) {
    Object.keys(variation).map((key) => {
      return variation[key]
    })
  }

  const variationValues =
    Object.keys(variation)
      .map((key) => variation[key])
      .join(', ') || ''

  return (
    <div className="grid grid-cols-12 py-6 first:pt-0 last:pb-0">
      <div className="col-span-4 aspect-[3/4] flex-shrink-0 overflow-hidden border border-gray-200 md:col-span-2">
        <Link to={permalink}>
          <ProductImage image={images} size={ImageSize.Thumbnail} />
        </Link>
      </div>
      <div className="col-span-8 ml-10 flex flex-1 flex-col md:col-span-10">
        <div>
          <div className="flex justify-between text-sm font-medium uppercase text-primary">
            <h3>
              {newArrival && <NewArrival />}
              <Link to={permalink}>{name}</Link>
              {variationValues && (
                <span className="block text-sm font-normal uppercase text-gray-500">
                  {variationValues}
                </span>
              )}
            </h3>
            <div className="flex">
              <Button
                className="py-0 leading-none"
                color="primary"
                disabled={isLoading}
                isLoading={isLoading}
                onClick={handleRemoveItem}
                size="sm"
                type="button"
                variant="light"
              >
                {__('Remove', 'asavagroup')}
              </Button>
            </div>
          </div>
        </div>
        <p className="mt-1 text-sm uppercase text-gray-500">{variation[0]}</p>
        <div className="mt-6 flex items-end justify-between font-medium uppercase text-primary">
          <div className="flex items-stretch">
            <Button
              className="disabled:cursor-not-allowed disabled:opacity-50"
              disabled={quantity === quantityLimits.minimum}
              isIconOnly
              onClick={() => handleUpdateItem(quantity - 1)}
              size="sm"
              variant="ghost"
            >
              <MinusIcon className="h-5 w-5" />
            </Button>
            <Input
              classNames={{
                input: 'text-center',
                inputWrapper: 'bg-white',
              }}
              color="primary"
              max={quantityLimits.maximum}
              min={quantityLimits.minimum}
              size="sm"
              type="number"
              value={quantity.toString()}
              variant="bordered"
            />
            <Button
              className="disabled:cursor-not-allowed disabled:opacity-50"
              disabled={quantity === quantityLimits.maximum}
              isIconOnly
              onClick={() => handleUpdateItem(quantity + 1)}
              size="sm"
              variant="ghost"
            >
              <PlusIcon className="h-5 w-5" />
            </Button>
          </div>
          <PriceDisplay
            price={prices.price}
            regularPrice={prices.regularPrice}
          />
        </div>
      </div>
    </div>
  )
}

export default CartItem
